'use client';

import { AskableIconLoading } from '@askable/ui/core/askable-icon';
import { TooltipProvider } from '@askable/ui/core/tooltip';
import { datadogRum } from '@datadog/browser-rum';
import Intercom, { shutdown, update } from '@intercom/messenger-js-sdk';
import { FeatureFlagsProvider, UserFeatureFlagProvider } from 'feature-flags';
import { Provider as JotaiProvider } from 'jotai';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { match, P } from 'ts-pattern';
import { Provider } from 'urql';

import { SetUpKinde } from '@/components/SetUpKinde/SetUpKinde';
import {
  currentPath,
  readPreviewSwitchState,
  writePreviewSwitchState,
} from '@/hooks/usePreviewTeamSwitch/previewSwitchState';
import i18n, { i18nReady } from '@/src/i18n';

import { AccessTokenSaver__DEPRECATED } from '../AccessTokenSaver_DEPRECATED';
import { DatadogInit } from '../DatadogInit';
import { QUERY_PARAM } from '../constants/constants';
import { ViewerContextProvider, useViewerContext } from '../context/ViewerContext/ViewerContext';
import { client } from '../network/client';
import { isDesktop } from '../utils/device-utils';
import { isEgressRoute } from '../utils/egress';
import { environment } from '../utils/environment-utils';
import { isInIframe } from '../utils/isInIframe';

import { MobileError } from './components/MobileError';

import type { ReactElement } from 'react';

const WHITE_LISTED_MOBILE_ROUTES = [
  'insights',
  'shares',
  'whoops',
  'ai-mod',
  'ai-moderated',
  'unmod',
  'survey',
  'prototype',
  'opt-out',
  'playback',
];

const logFeatureFlagEvaluation = (impression: { feature: string; treatment: string }) => {
  datadogRum.addFeatureFlagEvaluation(impression.feature, impression.treatment);
};

const MyApp = ({ children }: { children: ReactElement }) => {
  const router = useRouter();
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const { details, status, type, updateViewerStatus, intercomSettings, fetchingDetails } = useViewerContext();
  const previousUser = useRef<string | undefined>(undefined);
  const hasIntercomInit = useRef<boolean>(false);

  // Initialise Intercom with user details — skip when embedded in an iframe
  // (e.g. participant app's JIT tech-check flow) because Intercom's
  // cross-frame scripts trigger CORS errors against the parent origin.
  const inIframe = useMemo(() => isInIframe(), []);
  useEffect(() => {
    // If no intercomSettings, shutdown only if previously initialised
    if (inIframe || !intercomSettings) {
      if (hasIntercomInit.current) {
        shutdown();
      }
      previousUser.current = undefined;
      hasIntercomInit.current = false;
      return;
    }

    // First time init
    if (previousUser.current === undefined) {
      Intercom(intercomSettings);
      previousUser.current = details?._id;
      hasIntercomInit.current = true;
      return;
    }

    // User has changed, re-init
    if (previousUser.current !== details?._id) {
      shutdown();
      Intercom(intercomSettings);
      previousUser.current = details?._id;
      hasIntercomInit.current = true;
      return;
    }

    // Settings update for same user
    if (hasIntercomInit.current) {
      update(intercomSettings);
    }
  }, [intercomSettings, details?._id]);

  const shouldShowMobileError = useMemo(() => {
    const routeVal = pathName?.split('/') ?? [];
    const whiteListedRoutes = routeVal.some(v => WHITE_LISTED_MOBILE_ROUTES.includes(v));
    return !whiteListedRoutes && !isDesktop;
  }, [pathName]);

  useEffect(() => {
    // This useEffect might be triggered multiple times (e.g. when isReady is updated or router.replace updates route)
    // When user is on /whoops and tries to replace with /whoops, it will emit error
    // Takes user to whoops page if device type is not supported
    // We cannot call multiple router.replace in one process. (e.g. takes user to whoops page and then lobby page without token)
    // so make sure return the statement and two replaces are not called.
    if (shouldShowMobileError) {
      return;
    }

    updateViewerStatus();

    // Strip the one-time auth token so the JWT doesn't linger in history. Keep teamId: it's
    // not sensitive and carries the team context that makes preview links shareable.
    if (searchParams?.has(QUERY_PARAM.AUTH_TOKEN)) {
      router?.replace?.(window.location.pathname, undefined);
    }
  }, [router, pathName]);

  // Finishes a cross-team preview switch (see usePreviewTeamSwitch). The Kinde org swap can land on
  // the `/rooms` fallback rather than the preview, so bounce to the stashed path wherever we land.
  // Hard navigation, so a client-side redirect such as RootRedirect -> /rooms cannot race past it.
  // The stash survives the bounce. Only usePreviewTeamSwitch, running on the preview itself, can
  // tell whether the switch took. `bounced` keeps this to one hop per attempt.
  useEffect(() => {
    const pending = readPreviewSwitchState();
    if (!pending || pending.bounced) return;
    if (pending.returnPath === currentPath()) return;

    writePreviewSwitchState({ ...pending, bounced: true });
    window.location.replace(`${window.location.origin}${pending.returnPath}`);
  }, []);

  if (fetchingDetails) {
    return <AskableIconLoading isFullScreen />;
  }

  return (
    <div className="h-full">
      {status !== 'unknown' && (
        <UserFeatureFlagProvider
          app="sessions"
          userId={details?._id}
          userAttributes={{ type: type[0], team_id: details?.ConnectedTeam?._id ?? '' }}
          anonymousAttributes={{ demo: environment.isDemo }}
        >
          {({ isReady }) => {
            if (!isReady) {
              return <AskableIconLoading isFullScreen />;
            }
            return (
              <>
                {shouldShowMobileError ? <MobileError /> : null}
                {children}
              </>
            );
          }}
        </UserFeatureFlagProvider>
      )}
    </div>
  );
};

const WrappedProviders = props => {
  return (
    <SetUpKinde>
      <I18nextProvider i18n={i18n}>
        <Provider value={client}>
          <JotaiProvider>
            <TooltipProvider>
              <ViewerContextProvider>
                <Suspense fallback={<AskableIconLoading isFullScreen />}>
                  <MyApp {...props} />
                  <MetadataUpdate />
                </Suspense>
                <DatadogInit />
              </ViewerContextProvider>
            </TooltipProvider>
          </JotaiProvider>
        </Provider>
      </I18nextProvider>
    </SetUpKinde>
  );
};

const MetadataUpdate = () => {
  const { t } = useTranslation();
  const pathname = usePathname();
  useEffect(() => {
    const subtitle = match(pathname)
      .with(P.string.includes('settings'), () => t('meta.settings'))
      .with(P.string.includes('playback'), () => t('meta.playback'))
      .with(P.string.includes('rooms'), () => t('meta.rooms'))
      .with(P.string.includes('studies'), () => t('meta.studies'))
      .with(P.string.includes('insights'), () => t('meta.insights'))
      .with(P.string.includes('collections'), () => t('meta.collections'))
      .with(P.string.includes('ai-mod'), () => t('studyTypes.ai_moderated'))
      .otherwise(() => null);

    document.title = subtitle ? `${subtitle} | ${t('meta.title')}` : t('meta.title');
  }, [t, pathname]);
  return null;
};

// LiveKit egress browsers load `/lk-recording/*` headlessly with no auth context
// and a tight start-signal deadline. Auth, feature flags, viewer details, urql,
// and GTM-loaded analytics SDKs (Segment, FullStory, Hubble, Hotjar) are all dead
// weight on that path — and any of them stalling will tip the egress past LiveKit's
// "Start signal not received" timeout. Short-circuit to a minimal i18n + RUM tree.
//
// `DatadogInit` is intentionally NOT rendered here: its `IdentifyUser` child reads
// `useViewerContext()`, which would throw without `ViewerContextProvider`. The
// `datadogRum.init()` side-effect runs at module import time (see DatadogInit.tsx),
// so RUM is still initialised — we just skip the user-identify step on egress.
const EgressProviders = ({ children }: { children: ReactElement }) => (
  <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
);

let hasLoadedLocale = false;
void i18nReady.then(() => {
  hasLoadedLocale = true;
});

const subscribeToLocaleReady = (onReady: () => void) => {
  void i18nReady.then(onReady);
  return () => {};
};

// The server renders in English, so hydration starts as ready and matches the server HTML.
const useIsLocaleReady = () =>
  useSyncExternalStore(
    subscribeToLocaleReady,
    () => hasLoadedLocale,
    () => true,
  );

export const Providers = props => {
  const pathname = usePathname();
  const isLocaleReady = useIsLocaleReady();

  // Egress does not wait for the locale, because a remount can miss LiveKit's start signal.
  if (isEgressRoute(pathname)) {
    return <EgressProviders>{props.children}</EgressProviders>;
  }

  // The server renders English. The client then waits for the active locale before it renders.
  if (!isLocaleReady) {
    return <AskableIconLoading isFullScreen />;
  }

  return (
    <AccessTokenSaver__DEPRECATED>
      <FeatureFlagsProvider
        statsigClientKey={environment.statsigClientKey}
        environment={environment.statsigTier}
        statsigImpressionListener={logFeatureFlagEvaluation}
        app="sessions"
        attributes={{ demo: environment.isDemo }}
      >
        {({ isReady }) => {
          if (!isReady) {
            return <AskableIconLoading isFullScreen />;
          }

          return <WrappedProviders {...props} />;
        }}
      </FeatureFlagsProvider>
    </AccessTokenSaver__DEPRECATED>
  );
};
